import { makeAuthenticatedRequest } from '../services/api';

/**
 * Check Xero connection status
 * @returns {Promise<boolean>} - true if connected, false if not connected
 */
export const checkXeroConnection = async () => {
  try {
    // Use the dedicated connection status endpoint
    const response = await makeAuthenticatedRequest('/xero/connection/status', {
      method: 'GET',
    });
    
    console.log('Xero connection status response:', response);
    
    // Check if the response indicates we're connected
    // Handle both possible response formats
    if (response) {
      // Format 1: Direct response with isConnected
      if (response.isConnected !== undefined) {
        const isConnected = response.isConnected === true;
        console.log('Xero connection status:', isConnected ? 'connected' : 'disconnected');
        return isConnected;
      }
      // Format 2: Nested response with success and data
      else if (response.success && response.data && response.data.isConnected !== undefined) {
        const isConnected = response.data.isConnected === true;
        console.log('Xero connection status:', isConnected ? 'connected' : 'disconnected');
        return isConnected;
      }
    }
    
    console.log('Unexpected Xero connection status response format');
    return false;
  } catch (error) {
    console.error('Error checking Xero connection status:', error);
    return false;
  }
};

/**
 * Initiate Xero connection by redirecting to auth URL
 * @returns {Promise<string>} - Returns auth URL or throws error
 */
export const initiateXeroConnection = async () => {
  try {
    const response = await makeAuthenticatedRequest('/xero/get-auth-url', {
      method: 'GET',
    });

    if (response && response.authUrl) {
      return response.authUrl;
    } else {
      throw new Error('Backend did not return a valid authorization URL.');
    }
  } catch (error) {
    console.error('Error initiating Xero connection:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to initiate Xero connection.');
  }
};

/**
 * Handle Xero redirect status from URL parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {object|null} - Returns status object or null if no Xero params
 */
export const handleXeroRedirectStatus = (searchParams) => {
  const xeroStatus = searchParams.get('xero_status');
  const message = searchParams.get('message');

  if (!xeroStatus) {
    return null;
  }

  return {
    success: xeroStatus === 'success',
    status: xeroStatus,
    message: decodeURIComponent(message || (xeroStatus === 'success' ? 'Xero connected successfully!' : 'Failed to connect to Xero.'))
  };
};
